import {
  dayNearness,
  daysBetween,
  formatDayDe,
  todayInZurich,
  weekdayIndex,
  type Day,
} from "@vocifuchs/shared";
import { lists } from "../copy/lists.js";
import { Stars } from "./ui.js";

const copy = lists.progress;

/** How a child would say the day: heute, morgen, Freitag — or the plain date. */
export function whenLabel(day: Day, today: Day = todayInZurich()): string {
  switch (dayNearness(day, today)) {
    case "today":
      return copy.today;
    case "tomorrow":
      return copy.tomorrow;
    case "weekday":
      return copy.weekdays[weekdayIndex(day)] ?? formatDayDe(day);
    default:
      return formatDayDe(day);
  }
}

export function isExamAhead(dueDate: string | null, today: Day = todayInZurich()): boolean {
  return dueDate !== null && daysBetween(today, dueDate) >= 0;
}

/**
 * One list, one progress display. Before an exam the list shows readiness
 * against the exam goal; otherwise it shows stars. Never both on one card —
 * two progress displays side by side are one too many for a child
 * (docs/STARS.md §3).
 *
 * The readiness count gets a bar underneath: the same goal at a finer
 * resolution, because the count cannot move for the first two days of
 * practising, however well it goes (docs/STARS.md §8). The stars get no bar —
 * their third step asks for more than the bar measures, so a full bar next to
 * two stars would be the contradiction this document exists to remove.
 */
export function ListProgress({
  stars,
  ready,
  total,
  progress,
  dueDate,
  today = todayInZurich(),
}: {
  stars: 0 | 1 | 2 | 3;
  ready: number;
  total: number;
  /** Mean progress of every word towards sitting, 0 to 1. */
  progress: number;
  dueDate: string | null;
  today?: Day;
}) {
  if (dueDate === null || total === 0 || !isExamAhead(dueDate, today)) {
    return <Stars n={stars} label={copy.starsAria(stars)} />;
  }
  const when = whenLabel(dueDate, today);
  const pct = (value: number) => `${String(Math.min(100, Math.max(0, value * 100)))}%`;
  return (
    <span className="flex min-w-40 flex-col gap-1">
      {/* role="img" so the label is announced: on a bare span aria-label is
          prohibited and a screen reader would read nothing at all here. */}
      <span
        role="img"
        className="rounded-full bg-paper-2 px-3 py-1 text-sm font-semibold whitespace-nowrap text-ink-soft"
        aria-label={copy.readyAria(ready, total, when)}
      >
        <span aria-hidden>{copy.ready(ready, total, when)}</span>
      </span>
      {/* aria-hidden: the count above already says it, and a second
          announcement of the same goal only slows a screen reader down. */}
      <span aria-hidden className="relative mx-1 block h-1.5 overflow-hidden rounded-full bg-paper-2">
        <span
          className="absolute inset-y-0 left-0 rounded-full bg-fox/35 transition-[width] duration-500"
          style={{ width: pct(progress) }}
        />
        <span
          className="absolute inset-y-0 left-0 rounded-full bg-fox transition-[width] duration-500"
          style={{ width: pct(total === 0 ? 0 : ready / total) }}
        />
      </span>
    </span>
  );
}
